import { useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

interface ConfirmDiscardModalProps {
    isOpen: boolean;
    onCancel: () => void;
    onDiscard: () => void;
    onSaveDraft?: () => Promise<void>;
    isSaving?: boolean;
    saveDisabled?: boolean;
    saveDisabledHint?: string;
}

function ConfirmDiscardModal({
    isOpen,
    onCancel,
    onDiscard,
    onSaveDraft,
    isSaving = false,
    saveDisabled = false,
    saveDisabledHint
}: ConfirmDiscardModalProps) {
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSavingLocal, setIsSavingLocal] = useState(false);

    useEscapeKey(onCancel, isOpen && !isSaving && !isSavingLocal);

    if (!isOpen) return null;

    const handleSaveDraft = async () => {
        if (!onSaveDraft) return;
        setSaveError(null);
        setIsSavingLocal(true);
        try {
            await onSaveDraft();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to save draft';
            setSaveError(message);
        } finally {
            setIsSavingLocal(false);
        }
    };

    const saving = isSaving || isSavingLocal;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[440px] p-6 relative">
                <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">
                            {onSaveDraft ? 'Leave without saving?' : 'Discard progress?'}
                        </h3>
                    </div>
                    <button
                        onClick={onCancel}
                        disabled={saving}
                        className="text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50"
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <p className="text-sm text-gray-600 ml-12">
                    {onSaveDraft
                        ? 'Save a draft to continue later, or discard your changes entirely.'
                        : 'Your progress will be lost. Are you sure you want to leave?'}
                </p>

                {saveError && <p className="text-xs text-red-500 ml-12 mt-2">{saveError}</p>}
                {saveDisabled && saveDisabledHint && (
                    <p className="text-xs text-amber-600 ml-12 mt-2">{saveDisabledHint}</p>
                )}

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        onClick={onCancel}
                        disabled={saving}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {onSaveDraft ? 'Cancel' : 'Keep editing'}
                    </button>
                    <button
                        onClick={onDiscard}
                        disabled={saving}
                        className="px-4 py-2 text-sm font-semibold text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Discard
                    </button>
                    {onSaveDraft && (
                        <button
                            onClick={handleSaveDraft}
                            disabled={saving || saveDisabled}
                            title={saveDisabled ? saveDisabledHint : undefined}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving && (
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            )}
                            Save draft
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export { ConfirmDiscardModal };
